import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { BedDouble, Bath, Maximize } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ModelsSection = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [bedFilters, setBedFilters] = useState([]); // multi-select array
  const [sqftMax, setSqftMax] = useState(1200);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/products`);
        setModels((r.data.products || []).filter(p => p.is_online !== false));
        // Set initial sqft max
        const maxSqft = Math.max(...r.data.products.map(p => p.sqft), 1200);
        setSqftMax(maxSqft);
      } catch {}
    };
    load();
  }, []);

  const toggleBed = (val) => {
    if (val === 'all') { setBedFilters([]); return; }
    setBedFilters(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  const filtered = models.filter(m => {
    // Bedroom filter (multi-select)
    if (bedFilters.length > 0) {
      const hasMulti = bedFilters.includes('multi');
      const numFilters = bedFilters.filter(b => b !== 'multi').map(Number);
      const matchNum = numFilters.includes(m.bedrooms);
      const matchMulti = hasMulti && m.bedrooms >= 4;
      if (!matchNum && !matchMulti) return false;
    }
    // Sqft filter
    if (m.sqft > sqftMax) return false;
    return true;
  });

  const displayed = filtered.slice(0, 8);
  const allSqft = models.map(m => m.sqft);
  const minSqft = Math.min(...allSqft, 300);
  const maxSqftVal = Math.max(...allSqft, 1200);

  const bedButtons = [
    { val: 1, label: '1 Bedroom', labelZh: '1卧' },
    { val: 2, label: '2 Bedroom', labelZh: '2卧' },
    { val: 3, label: '3 Bedroom', labelZh: '3卧' },
    { val: 'multi', label: 'Multiple Bedroom', labelZh: '多卧' },
  ];

  return (
    <section id="models" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('models.title')}</h2>
          <p className="text-xl text-gray-600">{t('models.subtitle')}</p>
        </div>

        <div className="flex gap-8">
          {/* Left: Filters */}
          <div className="hidden lg:block w-48 flex-shrink-0">
            <div className="sticky top-24 space-y-5">
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{isZh ? '卧室' : 'Bedrooms'}</h4>
                <div className="space-y-1">
                  <button onClick={() => toggleBed('all')} className={`block w-full text-left px-3 py-1.5 rounded text-sm font-medium transition-all ${bedFilters.length === 0 ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                    {isZh ? '全部' : 'All'}
                  </button>
                  {bedButtons.map(b => {
                    const active = bedFilters.includes(b.val);
                    return (
                      <button key={b.val} onClick={() => toggleBed(b.val)} className={`block w-full text-left px-3 py-1.5 rounded text-sm font-medium transition-all ${active ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                        {isZh ? b.labelZh : b.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Sq.ft: <span className="text-red-600">{sqftMax}</span></h4>
                <input type="range" min={minSqft} max={maxSqftVal} step={50} value={sqftMax} onChange={e => setSqftMax(+e.target.value)} className="w-full accent-red-600" />
                <div className="flex justify-between text-xs text-gray-400 mt-1"><span>{minSqft}</span><span>{maxSqftVal}</span></div>
              </div>
            </div>
          </div>

          {/* Right: Product Grid — 4 per row */}
          <div className="flex-1">
            {/* Mobile filters */}
            <div className="lg:hidden flex flex-wrap gap-2 mb-6">
              <button onClick={() => toggleBed('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${bedFilters.length === 0 ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{isZh ? '全部' : 'All'}</button>
              {bedButtons.map(b => (
                <button key={b.val} onClick={() => toggleBed(b.val)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${bedFilters.includes(b.val) ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{isZh ? b.labelZh : b.label}</button>
              ))}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {displayed.map((model) => (
                <Card key={model.id} className="group overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  onClick={() => navigate(`/products/${model.id}`)} data-testid={`model-card-${model.id}`}>
                  <div className="relative overflow-hidden h-44">
                    <img src={model.images?.[0] || ''} alt={isZh ? model.title_zh : model.title_en} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    {model.vr_link && (
                      <a href={model.vr_link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded shadow hover:bg-red-700 z-10">VR</a>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">{isZh ? model.title_zh : model.title_en}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1"><BedDouble className="w-5 h-5 text-red-600" />{model.bedrooms}</span>
                      <span className="flex items-center gap-1"><Bath className="w-5 h-5 text-red-600" />{model.bathrooms}</span>
                      <span className="flex items-center gap-1"><Maximize className="w-5 h-5 text-red-600" />{model.sqft}</span>
                    </div>
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white text-sm py-2" onClick={e => { e.stopPropagation(); navigate(`/products/${model.id}`); }}>
                      {t('models.learnMore')}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-10 py-4 text-base rounded-lg shadow-lg"
                onClick={() => navigate('/floor-plans')}>
                {t('models.browseAll')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModelsSection;
